import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent, trackDoseWiseEvents } from '../utils/analytics';
import { PEN_STRENGTHS, CLICKS_PER_DOSE } from '../utils/constants';
import PenStatusBadge from '../components/PenStatusBadge';
import PenActions from '../components/PenActions';
import PenExpiryWarning from '../components/PenExpiryWarning';
import { partitionPensByStatus, STATUS_LABELS } from '../utils/penGrouping';
import PostDoseFeedbackPrompt from '../components/PostDoseFeedbackPrompt';
import { microPromptDue, markMicroPromptShown } from '../utils/feedback';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface Dose {
  _id: string;
  penStrength: number;
  clicks?: number;
  volume?: number;
  doseAmount: number;
  date: string;
  remainingClicks: number;
  remainingDoses: number;
  doseType?: 'standard' | 'golden_dose';
  extractionMethod?: 'pen_click' | 'volume_extraction';
  penId: string | { _id: string; name: string; strength: number };
  penName: string;
  penDurationText?: string;
  penStatus?: string;
}

interface Pen {
  _id: string;
  strength: number;
  startDate: string;
  remainingClicks: number;
  remainingDoses: number;
  status: 'active' | 'empty' | 'expired' | 'retired';
  name: string;
  costPerPen?: number;
  purchaseDate?: string;
  pharmacy?: string;
  // DOS-7 expiry annotations returned by GET /api/pens (DOS-25 caution reads these)
  daysUntilExpiration?: number | null;
  expiresAt?: string | null;
  expirationDays?: number;
  enableFifthDose: boolean;
  goldenDoseExtracted?: boolean;
  goldenDoseVolume?: number;
  goldenDoseAmount?: number;
  goldenDoseDate?: string;
  doses?: Array<{
    date: Date;
    clicks: number;
    doseAmount: number;
  }>;
}

// Utility function to get current local datetime string in yyyy-MM-ddTHH:mm
function getCurrentLocalDateTimeString(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

// Utility function to format a local datetime string for display
function formatLocalDateTime(dateString: string): string {
  const date = new Date(dateString);
  return `${date.toLocaleDateString('en-GB')} ${date.toLocaleTimeString('en-GB', { hour12: true, hour: '2-digit', minute: '2-digit' })}`;
}



function LogDosePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [clicks, setClicks] = useState(0);
  const [penStrength, setPenStrength] = useState(2.5);
  const [doseHistory, setDoseHistory] = useState<Dose[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(() => getCurrentLocalDateTimeString());
  const [pens, setPens] = useState<Pen[]>([]);
  const [selectedPenIndex, setSelectedPenIndex] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [showNewPenModal, setShowNewPenModal] = useState(false);
  const [newPenName, setNewPenName] = useState('');
  const [newPenStrength, setNewPenStrength] = useState(2.5);
  const [newPenStartDate, setNewPenStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newPenCost, setNewPenCost] = useState('');
  const [newPenPurchaseDate, setNewPenPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [newPenPharmacy, setNewPenPharmacy] = useState('');
  const [creatingPen, setCreatingPen] = useState(false);
  const [editDoseModal, setEditDoseModal] = useState<{ open: boolean; dose: Dose | null }>({ open: false, dose: null });
  const [editClicks, setEditClicks] = useState(0);
  const [editDate, setEditDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [missingCostPens, setMissingCostPens] = useState<Pen[]>([]);
  const [showCostModal, setShowCostModal] = useState(false);
  const [costUpdates, setCostUpdates] = useState<{ [penId: string]: { costPerPen: string; purchaseDate: string; pharmacy: string } }>({});

  // DOS-6: show inactive/retired pens only when expanded; persist across sessions
  const [showInactivePens, setShowInactivePens] = useState<boolean>(() => {
    try { return localStorage.getItem('dw_pens_show_inactive') === 'true'; } catch { return false; }
  });
  const toggleShowInactivePens = () => {
    setShowInactivePens(prev => {
      const next = !prev;
      try { localStorage.setItem('dw_pens_show_inactive', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // DOS-10: individual status groups can be collapsed independently; persist which are collapsed
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('dw_pens_collapsed_groups') || '{}'); } catch { return {}; }
  });
  const toggleGroupCollapsed = (status: string) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [status]: !prev[status] };
      try { localStorage.setItem('dw_pens_collapsed_groups', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const [newPenEnableFifthDose, setNewPenEnableFifthDose] = useState<boolean>(user?.defaultFifthDoseEnabled ?? false);
  const [goldenDoseLoading, setGoldenDoseLoading] = useState<{ [penId: string]: boolean }>({});
  const [goldenDoseError, setGoldenDoseError] = useState<{ [penId: string]: string }>({});
  const [isGoldenDose, setIsGoldenDose] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [retireConfirm, setRetireConfirm] = useState<{ open: boolean; pen: Pen | null }>({ open: false, pen: null });
  const [retiring, setRetiring] = useState(false);
  const [injectionLocation, setInjectionLocation] = useState<string>('abdomen');
  const [injectionSubLocation, setInjectionSubLocation] = useState<string>('left_lower');
  const [painLevel, setPainLevel] = useState<number>(0);
  

  const [goldenDoseVolume, setGoldenDoseVolume] = useState(0);
  const [showGoldenDoseEducation, setShowGoldenDoseEducation] = useState(false);
  const [hasSeenGoldenDoseEducation, setHasSeenGoldenDoseEducation] = useState(false);
  const [userCurrency, setUserCurrency] = useState('AUD');
  
  // Pagination state for dose history
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Pagination helper functions
  const totalPages = Math.ceil(doseHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDoses = doseHistory
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(startIndex, endIndex);
  
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };
  
  // Edit pen modal state
  const [showEditPenModal, setShowEditPenModal] = useState(false);
  const [editingPen, setEditingPen] = useState<Pen | null>(null);
  const [editPenName, setEditPenName] = useState('');
  const [editPenStartDate, setEditPenStartDate] = useState('');
  const [editPenCost, setEditPenCost] = useState('');
  const [editPenPurchaseDate, setEditPenPurchaseDate] = useState('');
  const [editPenPharmacy, setEditPenPharmacy] = useState('');
  const [savingEditPen, setSavingEditPen] = useState(false);
  const [editPenError, setEditPenError] = useState('');

  // Add state for doseAmount at the top of LogDosePage
  const [doseAmount, setDoseAmount] = useState<number>(0);

  // Add state to track which pen's toggle triggered the modal
  const [pendingGoldenDosePenId, setPendingGoldenDosePenId] = useState<string | null>(null);
  
  // Add state to track if main Golden Dose toggle triggered the modal
  const [pendingGoldenDoseToggle, setPendingGoldenDoseToggle] = useState<boolean | null>(null);

  // Add handler for dose input change
  const handleDoseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDose = Math.max(0, Number(e.target.value));
    setDoseAmount(newDose);
    if (isGoldenDose) {
      // Convert mg to mL for golden dose, capped to goldenDoseRemaining
      const vol = Math.min((newDose / penStrength) * 0.6, goldenDoseRemaining);
      setGoldenDoseVolume(Number(vol.toFixed(2)));
    } else {
      const newClicks = Math.round((newDose / penStrength) * CLICKS_PER_DOSE);
      setClicks(Math.min(60, Math.max(0, newClicks)));
    }
    trackDoseWiseEvents.doseLogged(newDose, penStrength, isGoldenDose);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newClicks = Math.min(60, Math.max(0, Math.round(Number(e.target.value))));
    setClicks(newClicks);
    const newDose = (newClicks / CLICKS_PER_DOSE) * penStrength;
    setDoseAmount(Number(newDose.toFixed(2)));
  };

  const fetchDoseHistory = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/doses`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error('Dose history data is not an array:', data);
        return;
      }

      const sortedDoses = data.sort((a: Dose, b: Dose) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setDoseHistory(sortedDoses);
    } catch (error) {
      console.error('Error fetching dose history:', error);
    }
  }, [isAuthenticated]);



  const fetchActivePens = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPens([]);
        setSelectedPenIndex(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/pens`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch pens: ${response.status}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        setPens([]);
        setSelectedPenIndex(null);
        return;
      }

      const sortedPens = data
        .filter((pen: Pen) => pen && typeof pen === 'object')
        .sort((a: Pen, b: Pen) => {
          const aStatus = a.status || 'empty';
          const bStatus = b.status || 'empty';
          if (aStatus === 'active' && bStatus !== 'active') return -1;
          if (aStatus !== 'active' && bStatus === 'active') return 1;
          const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
          return bDate - aDate;
        });

      setPens(sortedPens);

      const activePenIndex = sortedPens.findIndex((pen: Pen) => pen.status === 'active');
      if (activePenIndex !== -1) {
        setSelectedPenIndex(activePenIndex);
        setPenStrength(sortedPens[activePenIndex].strength);
      } else {
        setSelectedPenIndex(null);
      }
    } catch (error) {
      console.error('Error fetching pens:', error);
      setPens([]);
      setSelectedPenIndex(null);
    }
  }, [isAuthenticated]);

  const fetchUserCurrency = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUserCurrency(userData.currency || 'AUD');
      }
    } catch (error) {
      console.error('Error fetching user currency:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDoseHistory();
      fetchActivePens();
      fetchUserCurrency();
    }
  }, [isAuthenticated, fetchDoseHistory, fetchActivePens, fetchUserCurrency]);

  useEffect(() => {
    if (pens.length > 0) {
      const missing = pens.filter(pen => pen.costPerPen === undefined || pen.costPerPen === null || pen.purchaseDate === undefined || pen.purchaseDate === null);
      setMissingCostPens(missing);
      setShowCostModal(missing.length > 0);
      const updates: { [penId: string]: { costPerPen: string; purchaseDate: string; pharmacy: string } } = {};
      missing.forEach(pen => {
        updates[pen._id] = { costPerPen: '', purchaseDate: '', pharmacy: pen.pharmacy || '' };
      });
      setCostUpdates(updates);
    }
  }, [pens]);

  useEffect(() => {
    if (showNewPenModal && user) {
      setNewPenEnableFifthDose(user.defaultFifthDoseEnabled ?? false);
    }
  }, [showNewPenModal, user]);

  const calculateDose = () => {
    if (isGoldenDose) {
      return (goldenDoseVolume / 0.6) * penStrength;
    }
    return (clicks / CLICKS_PER_DOSE) * penStrength;
  };

  const doLogDose = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (selectedPenIndex === null) {
      alert('Please select a pen first');
      return;
    }

    const selectedPen = pens[selectedPenIndex];
    
    // Validate Golden Dose requirements
    if (isGoldenDose) {
      if (selectedPen.status !== 'empty') {
        alert('Golden Dose can only be extracted from empty pens');
        return;
      }
      if (!selectedPen.enableFifthDose) {
        alert('Golden Dose tracking is not enabled for this pen');
        return;
      }
    } else {
      // Standard dose validation
      if (selectedPen.remainingClicks < clicks) {
        alert('Not enough clicks remaining in the selected pen');
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/doses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          penId: selectedPen._id,
          clicks: isGoldenDose ? undefined : clicks,
          volume: isGoldenDose ? goldenDoseVolume : undefined,
          doseAmount: calculateDose(),
          date: new Date(selectedDate).toISOString(),
          doseType: isGoldenDose ? 'golden_dose' : 'standard',
          extractionMethod: isGoldenDose ? 'volume_extraction' : 'pen_click',
          injectionLocation: injectionLocation || undefined,
          injectionSubLocation: injectionLocation === 'abdomen' ? injectionSubLocation : undefined,
          painLevel,
        }),
      });

      if (!response.ok) {
        let errorMsg = 'Failed to log dose. Please try again.';
        try {
          const errData = await response.json();
          if (errData && errData.error) {
            errorMsg = errData.error;
          }
        } catch {}
        alert(errorMsg);
        return;
      }

      trackDoseWiseEvents.doseLogged(calculateDose(), penStrength, isGoldenDose);

      await fetchDoseHistory();
      await fetchActivePens();
      setCurrentPage(1); // Reset to first page to show the new dose
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      // DOS-38: occasional post-dose feedback micro-prompt (throttled to 14 days)
      if (microPromptDue()) {
        markMicroPromptShown();
        setShowFeedbackPrompt(true);
      }
      setInjectionLocation('abdomen');
      setInjectionSubLocation('left_lower');
      setPainLevel(0);
      setShowConfirm(false);
    } catch (error) {
      console.error('Error logging dose:', error);
      alert('Failed to log dose. Please try again.');
    }
  };



  // Helper to get pen info by id
  function getPenById(pens: Pen[], penId: string) {
    return pens.find((pen) => pen._id === penId);
  }



  // Create new pen handler (stubbed for now)
  const handleCreatePen = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingPen(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      if (!newPenName || !newPenStrength || !newPenStartDate || !newPenCost || !newPenPurchaseDate) {
        alert('Please fill in all required fields.');
        setCreatingPen(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/pens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newPenName,
          strength: newPenStrength,
          startDate: newPenStartDate,
          costPerPen: parseFloat(newPenCost),
          purchaseDate: newPenPurchaseDate,
          pharmacy: newPenPharmacy || undefined,
          enableFifthDose: newPenEnableFifthDose
        }),
      });
      if (!response.ok) throw new Error('Failed to create pen');
      
      trackDoseWiseEvents.penAdded(newPenStrength, parseFloat(newPenCost));

      setShowNewPenModal(false);
      setNewPenName('');
      setNewPenStrength(2.5);
      setNewPenStartDate(new Date().toISOString().slice(0, 10));
      setNewPenCost('');
      setNewPenPurchaseDate(new Date().toISOString().slice(0, 10));
      setNewPenPharmacy('');
      setNewPenEnableFifthDose(user?.defaultFifthDoseEnabled ?? false);
      await fetchActivePens();
    } catch (err) {
      alert('Failed to create pen.');
    } finally {
      setCreatingPen(false);
    }
  };

  // Open edit modal
  const openEditModal = (dose: Dose) => {
    setEditDoseModal({ open: true, dose });
    setEditClicks(dose.clicks || 0);
    setEditDate(new Date(dose.date).toISOString().slice(0, 16));
  };

  // Save dose edit
  const handleEditDose = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editDoseModal.dose) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${API_BASE_URL}/doses/${editDoseModal.dose._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clicks: editClicks,
          date: new Date(editDate).toISOString(),
        }),
      });
      if (!response.ok) throw new Error('Failed to update dose');
      
      trackEvent('dose_edited', {
        doseId: editDoseModal.dose._id,
        oldClicks: editDoseModal.dose.clicks,
        newClicks: editClicks,
        oldDate: editDoseModal.dose.date,
        newDate: editDate
      });

      setEditDoseModal({ open: false, dose: null });
      await fetchDoseHistory();
      await fetchActivePens();
      setCurrentPage(1); // Reset to first page to show the updated dose
    } catch (err) {
      alert('Failed to update dose.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCostUpdate = async (penId: string) => {
    const update = costUpdates[penId];
    if (!update.costPerPen || !update.purchaseDate) {
      alert('Please fill in all required fields.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${API_BASE_URL}/pens/${penId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          costPerPen: parseFloat(update.costPerPen),
          purchaseDate: update.purchaseDate,
          pharmacy: update.pharmacy || undefined,
        }),
      });
      if (!response.ok) throw new Error('Failed to update pen');
      await fetchActivePens();
    } catch (err) {
      alert('Failed to update pen.');
    }
  };

  // DOS-8: retire (archive) a pen — confirmed via the retire modal
  const handleRetirePen = async (penId: string) => {
    setRetiring(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${API_BASE_URL}/pens/${penId}/retire`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to retire pen');
      setRetireConfirm({ open: false, pen: null });
      await fetchActivePens();
    } catch (err) {
      alert('Failed to retire pen. Please try again.');
    } finally {
      setRetiring(false);
    }
  };

  // DOS-8: restore a retired pen back to active (non-destructive, no modal)
  const handleRestorePen = async (penId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${API_BASE_URL}/pens/${penId}/restore`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to restore pen');
      await fetchActivePens();
    } catch (err) {
      alert('Failed to restore pen. Please try again.');
    }
  };



  async function handleToggleGoldenDose(penId: string, current: boolean, skipEducation = false) {
    // Show education modal if enabling Golden Dose for the first time
    if (!current && !hasSeenGoldenDoseEducation && !skipEducation) {
      setShowGoldenDoseEducation(true);
      return;
    }
    setGoldenDoseLoading(ld => ({ ...ld, [penId]: true }));
    setGoldenDoseError(errs => ({ ...errs, [penId]: '' }));
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const response = await fetch(`${API_BASE_URL}/pens/${penId}/fifth-dose`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enableFifthDose: !current }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to update Golden Dose setting');
      }
      await fetchActivePens();
    } catch (err: any) {
      setGoldenDoseError(errs => ({ ...errs, [penId]: err.message || 'Failed to update' }));
    } finally {
      setGoldenDoseLoading(ld => ({ ...ld, [penId]: false }));
    }
  }

  const handleGoldenDoseToggle = (enabled: boolean) => {
    if (enabled && !hasSeenGoldenDoseEducation) {
      setShowGoldenDoseEducation(true);
      setPendingGoldenDoseToggle(enabled);
      return;
    }
    setIsGoldenDose(enabled);
    if (enabled) {
      // Switching to Golden Dose: set goldenDoseVolume and doseAmount based on clicks
      const vol = Math.min((clicks / CLICKS_PER_DOSE) * 0.6, goldenDoseRemaining);
      setGoldenDoseVolume(Number(vol.toFixed(2)));
      setDoseAmount(Number(((vol / 0.6) * penStrength).toFixed(2)));
    } else {
      // Switching to Standard Dose: set clicks and doseAmount based on goldenDoseVolume
      const newClicks = Math.round((goldenDoseVolume / 0.6) * CLICKS_PER_DOSE);
      setClicks(newClicks);
      setDoseAmount(Number(((newClicks / CLICKS_PER_DOSE) * penStrength).toFixed(2)));
    }
  };

  const handleGoldenDoseEducationConfirm = () => {
    setHasSeenGoldenDoseEducation(true);
    setShowGoldenDoseEducation(false);
    
    // Handle pen-specific toggle
    if (pendingGoldenDosePenId) {
      const pen = pens.find(p => p._id === pendingGoldenDosePenId);
      if (pen) {
        handleToggleGoldenDose(pen._id, pen.enableFifthDose, true); // Skip education since we're already in the modal
      }
      setPendingGoldenDosePenId(null);
    }
    
    // Handle main Golden Dose toggle
    if (pendingGoldenDoseToggle !== null) {
      setIsGoldenDose(pendingGoldenDoseToggle);
      if (pendingGoldenDoseToggle) {
        // Switching to Golden Dose: calculate goldenDoseRemaining fresh
        const selectedPen = selectedPenIndex !== null ? pens[selectedPenIndex] : null;
        const goldenDosesForPen = selectedPen && doseHistory.length > 0
          ? doseHistory.filter(d => d.doseType === 'golden_dose' && ((typeof d.penId === 'string' && d.penId === selectedPen._id) || (typeof d.penId === 'object' && d.penId._id === selectedPen._id)))
          : [];
        const goldenDoseUsed = goldenDosesForPen.reduce((sum, d) => sum + (d.volume || 0), 0);
        const freshGoldenDoseRemaining = Math.max(0, 0.6 - goldenDoseUsed);
        
        const vol = Math.min((clicks / CLICKS_PER_DOSE) * 0.6, freshGoldenDoseRemaining);
        setGoldenDoseVolume(Number(vol.toFixed(2)));
        setDoseAmount(Number(((vol / 0.6) * penStrength).toFixed(2)));
      } else {
        // Switching to Standard Dose: set clicks and doseAmount based on goldenDoseVolume
        const newClicks = Math.round((goldenDoseVolume / 0.6) * CLICKS_PER_DOSE);
        setClicks(newClicks);
        setDoseAmount(Number(((newClicks / CLICKS_PER_DOSE) * penStrength).toFixed(2)));
      }
      setPendingGoldenDoseToggle(null);
    }
  };

  const openEditPenModal = (pen: Pen) => {
    setEditingPen(pen);
    setEditPenName(pen.name);
    setEditPenStartDate(pen.startDate ? new Date(pen.startDate).toISOString().slice(0, 10) : '');
    setEditPenCost(pen.costPerPen?.toString() || '');
    setEditPenPurchaseDate(pen.purchaseDate ? new Date(pen.purchaseDate).toISOString().slice(0, 10) : '');
    setEditPenPharmacy(pen.pharmacy || '');
    setEditPenError('');
    setShowEditPenModal(true);
  };

  const deletePen = async (penId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`${API_BASE_URL}/pens/${penId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete pen');
      }
      
      await fetchActivePens();
      trackEvent('pen_deleted', { penId });
      
      // Reset selection if deleted pen was selected
      const deletedPenIndex = pens.findIndex(p => p._id === penId);
      if (selectedPenIndex === deletedPenIndex) {
        setSelectedPenIndex(null);
      }
    } catch (err) {
      alert(`Failed to delete pen: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleEditPen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPen) return;
    
    setSavingEditPen(true);
    setEditPenError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const response = await fetch(`${API_BASE_URL}/pens/${editingPen._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editPenName,
          startDate: editPenStartDate,
          costPerPen: editPenCost ? parseFloat(editPenCost) : undefined,
          purchaseDate: editPenPurchaseDate,
          pharmacy: editPenPharmacy || undefined,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update pen');
      }
      
      await fetchActivePens();
      setShowEditPenModal(false);
      setEditingPen(null);
      
      trackEvent('pen_edited', {
        penId: editingPen._id,
        penName: editPenName,
        hasExistingDoses: editingPen.doses && editingPen.doses.length > 0,
      });
      
    } catch (err) {
      setEditPenError(err instanceof Error ? err.message : 'Failed to update pen');
    } finally {
      setSavingEditPen(false);
    }
  };

  const selectedPen = selectedPenIndex !== null ? pens[selectedPenIndex] : null;

  // DOS-6: active pens shown by default; empty/expired/retired grouped behind the expander.
  // Original index preserved so selection (which keys off pens[index]) keeps working.
  const { active: activePens, inactive: inactivePens, groups: inactiveGroups } = partitionPensByStatus(pens);

  // The full pen card, rendered from both the active grid and the inactive groups.
  const renderPenCard = (pen: Pen, index: number) => (
    <div
      key={pen._id}
      role="button"
      tabIndex={0}
      onClick={() => {
        setSelectedPenIndex(index);
        setPenStrength(pen.strength);
        if (isGoldenDose && !pen.enableFifthDose) {
          setIsGoldenDose(false);
        }
      }}
      className={`group relative rounded-xl border-2 px-3 py-3 text-left shadow transition-all duration-150 w-full
        ${selectedPenIndex === index ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 bg-white'} hover:border-emerald-400`}
      style={{ cursor: 'pointer' }}
    >
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="font-semibold text-emerald-700">{pen.name}</span>
        <PenStatusBadge status={pen.status} />
        <PenExpiryWarning
          status={pen.status}
          daysUntilExpiration={pen.daysUntilExpiration}
          expirationDays={pen.expirationDays}
          expiresAt={pen.expiresAt}
        />
        {pen.enableFifthDose && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300" title="Golden Dose enabled: This pen is set to allow a Golden Dose extraction.">
            <svg className="w-4 h-4 mr-1 text-yellow-500 inline-block" fill="currentColor" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z"/></svg>
            Golden Dose
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{pen.strength}mg</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{pen.remainingClicks} clicks remaining</div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xs text-gray-600 dark:text-gray-300">Golden Dose:</span>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setSelectedPenIndex(index);
            setPenStrength(pen.strength);
            if (isGoldenDose && pen.enableFifthDose) {
              setIsGoldenDose(false);
            }
            if (!hasSeenGoldenDoseEducation) {
              setShowGoldenDoseEducation(true);
              setPendingGoldenDosePenId(pen._id);
              return;
            }
            handleToggleGoldenDose(pen._id, pen.enableFifthDose);
          }}
          className={`${pen.enableFifthDose ? 'bg-orange-400' : 'bg-stone-200'} relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
          role="switch"
          aria-checked={pen.enableFifthDose}
          disabled={goldenDoseLoading[pen._id]}
        >
          <span
            aria-hidden="true"
            className={`${pen.enableFifthDose ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
          />
        </button>
        {goldenDoseLoading[pen._id] && <span className="ml-2 text-xs text-yellow-600 animate-pulse">Saving...</span>}
        {goldenDoseError[pen._id] && <span className="ml-2 text-xs text-red-600">{goldenDoseError[pen._id]}</span>}
      </div>

      {/* Edit / Retire / Delete buttons on hover */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openEditPenModal(pen);
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs transition-colors"
          title="Edit pen"
        >
          ✏️
        </button>
        <PenActions
          status={pen.status}
          onRetire={() => setRetireConfirm({ open: true, pen })}
          onRestore={() => handleRestorePen(pen._id)}
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Are you sure you want to delete "${pen.name}"? This action cannot be undone.`)) {
              deletePen(pen._id);
            }
          }}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs transition-colors"
          title="Delete pen"
        >
          🗑️
        </button>
      </div>
    </div>
  );

  const goldenDosesForPen = selectedPen && doseHistory.length > 0
    ? doseHistory.filter(d => d.doseType === 'golden_dose' && ((typeof d.penId === 'string' && d.penId === selectedPen._id) || (typeof d.penId === 'object' && d.penId._id === selectedPen._id)))
    : [];
  const goldenDoseUsed = goldenDosesForPen.reduce((sum, d) => sum + (d.volume || 0), 0);
  const goldenDoseRemaining = Math.max(0, 0.6 - goldenDoseUsed);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h1 className="text-4xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent dark:text-white">
                Log Dose
              </h1>
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Please log in to record your doses</p>
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Log in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <h1 className="text-3xl sm:text-4xl font-black mb-6 font-['Space_Grotesk'] bg-gradient-to-r from-emerald-600 via-teal-600 to-orange-500 bg-clip-text text-transparent text-center">
              Mounjaro Dose Calculator
            </h1>
            
            {/* SECTION 1: DOSE LOGGING */}
            <div className="mb-8 sm:mb-12">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm mr-2 sm:mr-3">
                  1
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Dose Logging</h2>
              </div>
              
              {/* Pen Selection Cards (DOS-6: active by default, inactive/retired behind an expander) */}
              <div className="mb-8 flex flex-col items-center gap-3">
                {/* Active pens + Add New Pen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full max-w-4xl">
                  {activePens.map(({ pen, index }) => renderPenCard(pen, index))}
                  <button
                    onClick={() => {
                      setShowNewPenModal(true);
                    }}
                    className="rounded-xl border-2 border-dashed border-emerald-300 px-3 py-3 text-center text-emerald-600 bg-emerald-50 hover:bg-emerald-100 w-full min-h-[44px]"
                  >
                    + Add New Pen
                  </button>
                </div>

                {/* Expander toggle — only when there are inactive/retired pens */}
                {inactivePens.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleShowInactivePens}
                    className="text-sm font-medium text-stone-600 hover:text-emerald-700 px-3 py-2 min-h-[44px]"
                    aria-expanded={showInactivePens}
                  >
                    {showInactivePens
                      ? 'Hide inactive & retired pens ▴'
                      : `Show ${inactivePens.length} inactive & retired pen${inactivePens.length !== 1 ? 's' : ''} ▾`}
                  </button>
                )}

                {/* Grouped inactive/retired pens (shown when expanded) */}
                {showInactivePens && inactiveGroups.map(group => {
                  const collapsed = !!collapsedGroups[group.status];
                  return (
                    <div key={group.status} className="w-full max-w-4xl rounded-xl border border-stone-200 bg-stone-50/60 p-3">
                      {/* DOS-10: each status group collapses independently */}
                      <button
                        type="button"
                        onClick={() => toggleGroupCollapsed(group.status)}
                        aria-expanded={!collapsed}
                        className="w-full flex items-center gap-2 text-left"
                      >
                        <span className="text-xs text-stone-500 w-3">{collapsed ? '▸' : '▾'}</span>
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                          {STATUS_LABELS[group.status] || group.status}
                        </h4>
                        <span className="text-xs text-stone-400">({group.items.length})</span>
                        <div className="flex-grow border-t border-stone-200" />
                      </button>
                      {!collapsed && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                          {group.items.map(({ pen, index }) => renderPenCard(pen, index))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Basic Calculator Section */}
              <div className="mb-8 max-w-md mx-auto">
                {/* Dose Type Toggle */}
                <div className="mb-6 flex flex-col items-center">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Dose Type</label>
                  <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full">
                    <button
                      type="button"
                      onClick={() => handleGoldenDoseToggle(false)}
                      className={`px-4 py-3 rounded-lg font-medium transition-colors w-full sm:w-auto ${
                        !isGoldenDose 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                      }`}
                    >
                      Standard Dose
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedPen?.enableFifthDose) {
                          handleGoldenDoseToggle(true);
                        }
                      }}
                      disabled={!selectedPen?.enableFifthDose}
                      className={`px-4 py-3 rounded-lg font-medium transition-colors w-full sm:w-auto ${
                        !selectedPen?.enableFifthDose
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : isGoldenDose 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                      }`}
                      title={
                        !selectedPen 
                          ? 'Select a pen first' 
                          : !selectedPen.enableFifthDose 
                            ? 'Enable Golden Dose on the selected pen first' 
                            : ''
                      }
                    >
                      Golden Dose
                    </button>
                  </div>
                  {!selectedPen?.enableFifthDose && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {!selectedPen 
                        ? 'Select a pen to enable dose logging' 
                        : 'Enable Golden Dose on the selected pen to use this option'}
                    </p>
                  )}
                </div>

                {/* Clicks Section (only for Standard Dose) */}
                {!isGoldenDose && (
                  <div className="mb-6">
                    <label className="block text-lg font-bold text-stone-700 mb-2 text-center">
                      Number of Clicks: <span className="text-3xl text-emerald-700">{clicks}</span>
                    </label>
                    <div className="relative h-16 bg-white dark:bg-gray-700 rounded-full overflow-visible max-w-md mx-auto py-3">
                      {/* Background track */}
                      <div className="absolute inset-0 h-12 top-2 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full z-0"></div>
                      {/* Slider input */}
                      <input
                        type="range"
                        min="0"
                        max="60"
                        value={clicks}
                        onChange={handleSliderChange}
                        onMouseDown={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.style.cursor = 'grabbing';
                        }}
                        onMouseUp={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.style.cursor = 'pointer';
                        }}
                        className="absolute inset-0 w-full h-12 opacity-0 cursor-pointer transition-all duration-150 ease-in-out z-30"
                        style={{
                          WebkitAppearance: 'none',
                          appearance: 'none',
                          background: 'transparent',
                        }}
                      />
                      {/* Visual thumb centered and in foreground */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-16 h-16 bg-emerald-600 rounded-full shadow-lg transform -translate-x-1/2 transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 pointer-events-none z-40 border-4 border-white"
                        style={{ 
                          left: `${(clicks / 60) * 100}%`,
                        }}
                      >
                        <div className="absolute inset-2 bg-emerald-400 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Volume Section (only for Golden Dose) */}
                {isGoldenDose && (
                  <div className="mb-6">
                    <label className="block text-lg font-bold text-gray-700 dark:text-gray-100 mb-2 text-center">
                      Volume: <span className="text-3xl text-yellow-600">{goldenDoseVolume.toFixed(2)} mL</span>
                    </label>
                    <div className="text-center text-lg font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-lg px-4 py-2 mb-2 shadow">
                      Golden Dose remaining for this pen: {goldenDoseRemaining.toFixed(2)} mL
                    </div>
                    <div className="relative h-16 bg-white dark:bg-gray-700 rounded-full overflow-visible max-w-md mx-auto py-3">
                      {/* Background track */}
                      <div className="absolute inset-0 h-12 top-2 bg-gradient-to-r from-yellow-100 to-yellow-300 rounded-full z-0"></div>
                      {/* Slider input */}
                      <input
                        type="range"
                        min="0"
                        max={goldenDoseRemaining}
                        step="0.01"
                        value={goldenDoseVolume}
                        onChange={(e) => {
                          const vol = Math.min(parseFloat(e.target.value), goldenDoseRemaining);
                          setGoldenDoseVolume(vol);
                          setDoseAmount(Number(((vol / 0.6) * penStrength).toFixed(2)));
                        }}
                        onMouseDown={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.style.cursor = 'grabbing';
                        }}
                        onMouseUp={(e) => {
                          const input = e.target as HTMLInputElement;
                          input.style.cursor = 'pointer';
                        }}
                        className="absolute inset-0 w-full h-12 opacity-0 cursor-pointer transition-all duration-150 ease-in-out z-30"
                        style={{
                          WebkitAppearance: 'none',
                          appearance: 'none',
                          background: 'transparent',
                        }}
                      />
                      {/* Visual thumb centered and in foreground */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-16 h-16 bg-yellow-500 rounded-full shadow-lg transform -translate-x-1/2 transition-all duration-150 ease-in-out hover:scale-110 active:scale-95 pointer-events-none z-40 border-4 border-white"
                        style={{ 
                          left: `${(goldenDoseVolume / (goldenDoseRemaining || 0.6)) * 100}%`,
                        }}
                      >
                        <div className="absolute inset-2 bg-yellow-400 rounded-full"></div>
                      </div>
                    </div>
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
                      Full pen = 0.6 mL • Golden Dose range: 0 - 0.6 mL
                    </div>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center gap-2 mb-4 w-full">
                  <label className="block text-sm font-semibold text-stone-700 mb-2 uppercase tracking-wider">Dose Amount</label>
                  <div className="flex items-center justify-center w-full">
                    <div className="relative w-full max-w-xs">
                      <input
                        className="text-2xl sm:text-4xl font-black font-['Space_Grotesk'] text-emerald-700 bg-white px-4 sm:px-8 py-4 sm:py-6 pr-12 sm:pr-16 rounded-2xl text-center w-full border-2 border-emerald-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400 shadow-md outline-none transition-all duration-200 placeholder-emerald-700"
                        type="number"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        min="0"
                        step="0.01"
                        value={doseAmount === 0 ? '0' : String(doseAmount).replace(/^0+(?!\.)/, '')}
                        onChange={handleDoseInputChange}
                        style={{ appearance: 'textfield' }}
                      />
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 text-2xl font-black text-emerald-700 pointer-events-none select-none">mg</span>
                    </div>
                  </div>
                </div>
                {/* Date/Time Picker */}
                <div className="mb-6 flex flex-col items-center">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Date & Time</label>
                  <input
                    type="datetime-local"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    className="border-2 border-emerald-200 rounded-2xl px-3 sm:px-4 py-3 text-lg sm:text-xl font-black font-['Space_Grotesk'] text-emerald-700 w-full max-w-xs text-center focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-gray-600 dark:text-white placeholder-emerald-700 transition-all duration-200 shadow-md mx-auto"
                  />
                </div>


                {/* Log Dose Button */}
                <div className="text-center">
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors duration-200 min-h-[44px]"
                  >
                    Log Dose
                  </button>
                </div>

                {/* Success Message */}
                {showSuccess && (
                  <div className="mt-4 text-center text-emerald-600">
                    Dose logged successfully!
                  </div>
                )}
              </div>
            </div>



            {/* SECTION 2: DOSE HISTORY */}
            <div className="mb-8">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                  2
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dose History</h2>
              </div>
              
              <div className="bg-teal-50 rounded-lg p-6 border border-teal-200 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Your Medication Journey</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Track your dosing history, see patterns over time, and monitor your progress. Each dose is grouped by pen for easy reference.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mx-auto max-w-2xl">
                {/* Show paginated doses */}
                {currentDoses.map((dose) => {
                    const pen = typeof dose.penId === 'string' 
                      ? getPenById(pens, dose.penId)
                      : dose.penId;
                    if (!pen) return null;
                    
                    return (
                      <div key={dose._id} className="mb-6">
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-6 text-center shadow-sm">
                          <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                            {/* No status badge here (DOS-24): this is a historical dose,
                                so the pen's live status would misrepresent it. */}
                            <span className="text-sm font-medium text-emerald-700">
                              {pen.name} ({pen.strength}mg)
                            </span>
                          </div>
                          <div className="text-lg font-semibold text-blue-400 mb-2">
                            {formatLocalDateTime(dose.date)}
                          </div>
                          <div className="text-gray-700 dark:text-gray-300 mb-1">
                            Dose: <span className="font-medium">{dose.doseAmount.toFixed(2)}mg</span>
                            {dose.doseType === 'golden_dose' && dose.volume ? (
                              <> | Volume: <span className="font-medium text-yellow-600">{dose.volume.toFixed(2)} mL</span></>
                            ) : (
                              <> | Clicks Used: <span className="font-medium">{dose.clicks}</span></>
                            )}
                            {('injectionLocation' in dose) && (dose as any).injectionLocation && (
                              <> | Location: <span className="font-medium capitalize">{(dose as any).injectionLocation}</span></>
                            )}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 mb-3">
                            Remaining: <span className="font-medium">{dose.remainingClicks} clicks</span> ({dose.remainingDoses} dose{dose.remainingDoses !== 1 ? 's' : ''})
                          </div>
                          <button
                            className="mt-2 px-4 py-1 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 transition"
                            onClick={() => openEditModal(dose)}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                
                {/* Pagination Controls */}
                {doseHistory.length > itemsPerPage && (
                  <div className="flex items-center justify-center mt-6 space-x-4">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                          : 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Page</span>
                      <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 rounded-lg font-medium">
                        {currentPage} of {totalPages}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        ({doseHistory.length} total doses)
                      </span>
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
                          : 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>

                         {/* New Pen Modal */}
             {showNewPenModal && (
               <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40">
                 <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-lg relative">
                   <button
                     className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                     onClick={() => setShowNewPenModal(false)}
                   >
                     ×
                   </button>
                   <h2 className="text-xl font-semibold mb-4 text-emerald-700">Add New Pen</h2>
                  <form onSubmit={handleCreatePen} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pen Name</label>
                      <input
                        type="text"
                        value={newPenName}
                        onChange={e => setNewPenName(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                                         <div>
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Strength (mg)</label>
                       <select
                         value={newPenStrength}
                         onChange={e => {
                           const strength = Number(e.target.value);
                           setNewPenStrength(strength);

                         }}
                         className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                       >
                         {PEN_STRENGTHS.map((str: number) => (
                           <option key={str} value={str}>{str} mg</option>
                         ))}
                       </select>
                     </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={newPenStartDate}
                        onChange={e => setNewPenStartDate(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cost per Pen ({userCurrency})</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newPenCost}
                        onChange={e => setNewPenCost(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g. 399.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchase Date</label>
                      <input
                        type="date"
                        value={newPenPurchaseDate}
                        onChange={e => setNewPenPurchaseDate(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pharmacy (optional)</label>
                      <input
                        type="text"
                        value={newPenPharmacy}
                        onChange={e => setNewPenPharmacy(e.target.value)}
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g. Chemist Warehouse"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enable Golden Dose</label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setNewPenEnableFifthDose(v => !v)}
                          className={`${newPenEnableFifthDose ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                          role="switch"
                          aria-checked={newPenEnableFifthDose}
                        >
                          <span
                            aria-hidden="true"
                            className={`${newPenEnableFifthDose ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                          />
                        </button>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {newPenEnableFifthDose ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Allow tracking of a Golden Dose from this pen (beyond the labeled 4 doses). This is against manufacturer recommendations but commonly practiced.
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={creatingPen}
                      className="w-full bg-emerald-600 text-white py-2 rounded font-semibold hover:bg-emerald-700 transition"
                    >
                      {creatingPen ? 'Creating...' : 'Create Pen'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Dose Modal */}
            {editDoseModal.open && editDoseModal.dose && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-md relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                    onClick={() => setEditDoseModal({ open: false, dose: null })}
                  >
                    ×
                  </button>
                  <h2 className="text-xl font-semibold mb-4 text-emerald-700">Edit Dose</h2>
                  <form onSubmit={handleEditDose} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Clicks</label>
                      <input
                        type="number"
                        min={1}
                        max={60}
                        value={editClicks}
                        onChange={e => setEditClicks(Number(e.target.value))}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date & Time</label>
                      <input
                        type="datetime-local"
                        value={editDate}
                        onChange={e => setEditDate(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={savingEdit}
                        className="flex-1 bg-emerald-600 text-white py-2 rounded font-semibold hover:bg-emerald-700 transition"
                      >
                        {savingEdit ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        className="flex-1 bg-red-500 text-white py-2 rounded font-semibold hover:bg-red-600 transition"
                        onClick={async () => {
                          if (!editDoseModal.dose) return;
                          if (!window.confirm('Are you sure you want to delete this dose? This action cannot be undone.')) return;
                          try {
                            const token = localStorage.getItem('token');
                            if (!token) throw new Error('No authentication token found');
                            await fetch(`${API_BASE_URL}/doses/${editDoseModal.dose._id}`, {
                              method: 'DELETE',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                              },
                            });
                            setEditDoseModal({ open: false, dose: null });
                            await fetchDoseHistory();
                            await fetchActivePens();
                          } catch (err) {
                            alert('Failed to delete dose.');
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* DOS-8: Retire Pen confirmation */}
            {retireConfirm.open && retireConfirm.pen && (
              <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                    onClick={() => setRetireConfirm({ open: false, pen: null })}
                  >
                    ×
                  </button>
                  <h3 className="text-xl font-semibold mb-4 text-emerald-700">Retire pen?</h3>
                  <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                    <p>
                      Retire <b>{retireConfirm.pen.name}</b>? It will be archived and moved out
                      of your active pens.
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                      Your dose history and cost savings are preserved — you can restore this
                      pen anytime.
                    </p>
                  </div>
                  <div className="mt-6 flex gap-2 justify-end">
                    <button
                      className="px-4 py-2 rounded bg-stone-200 dark:bg-gray-700"
                      onClick={() => setRetireConfirm({ open: false, pen: null })}
                      disabled={retiring}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 rounded bg-stone-600 hover:bg-stone-700 text-white disabled:opacity-60"
                      onClick={() => retireConfirm.pen && handleRetirePen(retireConfirm.pen._id)}
                      disabled={retiring}
                    >
                      {retiring ? 'Retiring…' : 'Retire pen'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirm Log Dose Modal */}
            {showConfirm && (
              <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirm(false)}
                  >
                    ×
                  </button>
                  <h3 className="text-xl font-semibold mb-4 text-emerald-700">Confirm Dose</h3>
                  <div className="space-y-4">
                    {selectedPenIndex !== null && pens[selectedPenIndex] && (
                      <PenExpiryWarning
                        variant="inline"
                        status={pens[selectedPenIndex].status}
                        daysUntilExpiration={pens[selectedPenIndex].daysUntilExpiration}
                        expirationDays={pens[selectedPenIndex].expirationDays}
                      />
                    )}
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      You are about to log a dose of <b>{calculateDose().toFixed(2)} mg</b>
                      {isGoldenDose ? (
                        <> (Golden Dose: <b>{goldenDoseVolume.toFixed(2)} mL</b>)</>
                      ) : (
                        <> (<b>{clicks}</b> clicks)</>
                      )} on {formatLocalDateTime(new Date(selectedDate).toString())}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Injection Location</label>
                      <select
                        value={injectionLocation}
                        onChange={e => setInjectionLocation(e.target.value)}
                        className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="abdomen">Abdomen</option>
                        <option value="thigh">Thigh</option>
                        <option value="arm">Arm</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    {injectionLocation === 'abdomen' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Abdomen Area</label>
                        <select
                          value={injectionSubLocation}
                          onChange={e => setInjectionSubLocation(e.target.value)}
                          className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="left_lower">Left - Lower</option>
                          <option value="left_upper">Left - Upper</option>
                          <option value="right_lower">Right - Lower</option>
                          <option value="right_upper">Right - Upper</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pain at Injection (0-10)</label>
                      <input
                        type="range"
                        min={0}
                        max={10}
                        step={1}
                        value={painLevel}
                        onChange={(e) => setPainLevel(parseInt(e.target.value))}
                        className="w-full"
                      />
                      <div className="text-right text-sm text-gray-600 dark:text-gray-400">{painLevel}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2 justify-end">
                    <button className="px-4 py-2 rounded bg-stone-200 dark:bg-gray-700" onClick={() => setShowConfirm(false)}>Cancel</button>
                    <button className="px-4 py-2 rounded bg-emerald-600 text-white" onClick={doLogDose}>Confirm Log Dose</button>
                  </div>
                </div>
              </div>
            )}

            {/* Cost Update Modal */}
            {showCostModal && missingCostPens.length > 0 && (
              <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-lg relative">
                  <h2 className="text-xl font-semibold mb-4 text-emerald-700">Update Pen Cost Data</h2>
                  <p className="mb-4 text-gray-700 dark:text-gray-300">To provide accurate cost analytics, please enter the cost and purchase date for your pen(s):</p>
                  {missingCostPens.map(pen => (
                    <div key={pen._id} className="mb-6 border-b pb-4 last:border-0 last:pb-0">
                      <div className="mb-2 font-semibold text-purple-600 dark:text-purple-400">{pen.name} ({pen.strength}mg, started {pen.startDate ? new Date(pen.startDate).toLocaleDateString() : ''})</div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={`Cost per Pen (${userCurrency})`}
                          value={costUpdates[pen._id]?.costPerPen || ''}
                          onChange={e => setCostUpdates(upd => ({ ...upd, [pen._id]: { ...upd[pen._id], costPerPen: e.target.value } }))}
                          className="border rounded px-3 py-2 flex-1 min-w-0 w-full dark:bg-gray-700 dark:text-white"
                          required
                        />
                        <input
                          type="date"
                          placeholder="Purchase Date"
                          value={costUpdates[pen._id]?.purchaseDate || ''}
                          onChange={e => setCostUpdates(upd => ({ ...upd, [pen._id]: { ...upd[pen._id], purchaseDate: e.target.value } }))}
                          className="border rounded px-3 py-2 flex-1 min-w-0 w-full dark:bg-gray-700 dark:text-white"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Pharmacy (optional)"
                          value={costUpdates[pen._id]?.pharmacy || ''}
                          onChange={e => setCostUpdates(upd => ({ ...upd, [pen._id]: { ...upd[pen._id], pharmacy: e.target.value } }))}
                          className="border rounded px-3 py-2 flex-1 min-w-0 w-full dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <button
                        className="mt-2 bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700"
                        onClick={() => handleCostUpdate(pen._id)}
                      >
                        Save
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 w-full">
                    <button
                      className="flex-1 bg-stone-200 text-stone-700 px-4 py-2 rounded hover:bg-stone-300 border border-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                      onClick={() => setShowCostModal(false)}
                    >
                      Not now – remind me later
                    </button>
                  </div>
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl"
                    onClick={() => setShowCostModal(false)}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* Golden Dose Education Modal */}
            {showGoldenDoseEducation && (
              <div 
                className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-50"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    // Clicked on backdrop
                    setShowGoldenDoseEducation(false);
                    setPendingGoldenDosePenId(null);
                    setPendingGoldenDoseToggle(null);
                  }
                }}
              >
                <div 
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z"/>
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Golden Dose Information</h2>
                    <p className="text-gray-600 dark:text-gray-400">Important information about Golden Dose extraction</p>
                  </div>

                  <div className="space-y-6 text-left">
                    {/* What is Golden Dose */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">What is a Golden Dose?</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        A Golden Dose is medication extracted from an empty pen beyond the labeled 4 doses. 
                        This involves manually extracting remaining medication using precise volume measurements.
                      </p>
                    </div>

                    {/* Safety Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">⚠️ Important Safety Information</h3>
                      <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-4">
                        <ul className="text-red-800 dark:text-red-200 space-y-2 text-sm">
                          <li>• <strong>Not recommended by manufacturer:</strong> This practice goes against official guidelines</li>
                          <li>• <strong>Sterility concerns:</strong> Manual extraction may compromise medication sterility</li>
                          <li>• <strong>Dosing accuracy:</strong> Volume-based dosing requires precise measurement</li>
                          <li>• <strong>Consult your healthcare provider:</strong> Always discuss with your doctor first</li>
                        </ul>
                      </div>
                    </div>

                    {/* Best Practices */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Best Practices (if proceeding)</h3>
                      <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                        <ul className="text-blue-800 dark:text-blue-200 space-y-2 text-sm">
                          <li>• Use sterile syringes and needles for extraction</li>
                          <li>• Measure volume precisely (0.1-0.6 mL range)</li>
                          <li>• Only extract from pens that are completely empty</li>
                          <li>• Store extracted medication properly and use promptly</li>
                          <li>• Monitor for any unusual side effects</li>
                        </ul>
                      </div>
                    </div>

                    {/* Cost Benefits */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Potential Cost Benefits</h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        Golden Doses can reduce medication costs by 15-25% by extracting additional doses 
                        from pens that would otherwise be discarded. The app tracks these savings accurately 
                        based on the volume extracted.
                      </p>
                    </div>

                    {/* Disclaimer */}
                    <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        <strong>Disclaimer:</strong> This app provides tracking tools only. Users are responsible 
                        for medication management decisions. Always consult healthcare providers before making 
                        changes to your medication routine. The app cannot guarantee medical outcomes or safety.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <button
                      onClick={() => {
                        setShowGoldenDoseEducation(false);
                        setPendingGoldenDosePenId(null);
                        setPendingGoldenDoseToggle(null);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGoldenDoseEducationConfirm}
                      className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      I Understand - Enable Golden Dose
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Pen Modal */}
            {showEditPenModal && editingPen && (
              <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 w-full max-w-lg relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowEditPenModal(false)}
                  >
                    ×
                  </button>
                  <h2 className="text-xl font-semibold mb-4 text-emerald-700">Edit Pen</h2>
                  <form onSubmit={handleEditPen} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pen Name</label>
                      <input
                        type="text"
                        value={editPenName}
                        onChange={e => setEditPenName(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={editPenStartDate}
                        onChange={e => setEditPenStartDate(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                      {editingPen.doses && editingPen.doses.length > 0 && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠️ This pen has existing doses. Start date must be before the earliest dose date.
                        </p>
                      )}
                    </div>
                    <div>
                                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cost per Pen ({userCurrency})</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editPenCost}
                        onChange={e => setEditPenCost(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g. 399.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Purchase Date</label>
                      <input
                        type="date"
                        value={editPenPurchaseDate}
                        onChange={e => setEditPenPurchaseDate(e.target.value)}
                        required
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pharmacy (optional)</label>
                      <input
                        type="text"
                        value={editPenPharmacy}
                        onChange={e => setEditPenPharmacy(e.target.value)}
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g. Chemist Warehouse"
                      />
                    </div>
                    
                    {editPenError && (
                      <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg p-3">
                        <p className="text-red-800 dark:text-red-200 text-sm">{editPenError}</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={savingEditPen}
                        className="flex-1 bg-emerald-600 text-white py-2 rounded font-semibold hover:bg-emerald-700 transition"
                      >
                        {savingEditPen ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEditPenModal(false)}
                        className="flex-1 bg-stone-200 text-stone-700 py-2 rounded font-semibold hover:bg-stone-300 transition dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <PostDoseFeedbackPrompt open={showFeedbackPrompt} onClose={() => setShowFeedbackPrompt(false)} />
    </div>
  );
}

export default LogDosePage; 